// Chuyển giá trị giữa AppSheet API và JavaScript.
//
// API trả mọi giá trị dạng chuỗi: ngày theo MM/DD/YYYY ("01/31/2026"), Yes/No là "True"/"False",
// số không có dấu phân cách hàng nghìn. Đọc ngày bằng new Date(chuỗi) thì phụ thuộc trình duyệt và
// dễ nhầm ngày/tháng, nên mọi chỗ trong module lương đọc qua các hàm ở đây.

const pad = (value, length = 2) => String(value).padStart(length, '0');

function buildDate(year, month, day, hours = 0, minutes = 0, seconds = 0) {
    const date = new Date(year, month - 1, day, hours, minutes, seconds);
    // Loại ngày không tồn tại như 02/31: Date tự lăn sang tháng sau thay vì báo lỗi.
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
    return date;
}

/** Đọc ngày từ AppSheet: MM/DD/YYYY [HH:mm[:ss]] hoặc YYYY-MM-DD[THH:mm[:ss]]. Không đọc được trả null. */
export function parseSheetDate(value) {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const text = String(value).trim();
    let match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (match) {
        const [, month, day, year, hours = 0, minutes = 0, seconds = 0] = match;
        return buildDate(+year, +month, +day, +hours, +minutes, +seconds);
    }
    match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
    if (match) {
        const [, year, month, day, hours = 0, minutes = 0, seconds = 0] = match;
        return buildDate(+year, +month, +day, +hours, +minutes, +seconds);
    }
    return null;
}

/** Ghi ngày ra AppSheet theo YYYY-MM-DD — dạng không thể nhầm ngày với tháng, các trang hiện tại cũng ghi như vậy. */
export function toSheetDate(value) {
    const date = value instanceof Date ? value : parseSheetDate(value);
    if (!date) return '';
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Ghi ngày giờ ra AppSheet theo MM/DD/YYYY HH:mm:ss, đúng dạng API trả về. */
export function toSheetDateTime(value) {
    const date = value instanceof Date ? value : parseSheetDate(value);
    if (!date) return '';
    return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/**
 * Đọc số. Chấp nhận cả "1.5" lẫn "1,5" (Sheet định dạng Việt Nam dùng dấu phẩy thập phân) và bỏ
 * dấu phân cách hàng nghìn. Ô trống trả fallback; chuỗi không phải số trả NaN để nơi gọi báo lỗi.
 */
export function parseSheetNumber(value, fallback = 0) {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'number') return value;
    let text = String(value).trim().replace(/\s/g, '');
    if (text === '') return fallback;
    const lastDot = text.lastIndexOf('.');
    const lastComma = text.lastIndexOf(',');
    if (lastDot >= 0 && lastComma >= 0) {
        // Có cả hai: dấu xuất hiện sau cùng là dấu thập phân.
        const decimal = lastDot > lastComma ? '.' : ',';
        const thousands = decimal === '.' ? ',' : '.';
        text = text.split(thousands).join('').replace(decimal, '.');
    } else if (lastComma >= 0) {
        // Chỉ có phẩy: một dấu và không đúng 3 chữ số phía sau là thập phân ("1,5"), còn lại là hàng nghìn.
        const parts = text.split(',');
        text = parts.length === 2 && parts[1].length !== 3 ? parts.join('.') : parts.join('');
    } else if ((text.match(/\./g) || []).length > 1) {
        text = text.split('.').join('');
    }
    return /^[-+]?\d*\.?\d+$/.test(text) ? Number(text) : NaN;
}

export function parseSheetBoolean(value) {
    if (typeof value === 'boolean') return value;
    return ['true', 'y', 'yes', '1'].includes(String(value ?? '').trim().toLowerCase());
}

/** Đọc cột JSON (LongText). Ô trống hoặc JSON hỏng trả fallback thay vì làm vỡ cả trang. */
export function parseSheetJson(value, fallback) {
    if (value === null || value === undefined || String(value).trim() === '') return fallback;
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
}

export function toSheetJson(value) {
    return JSON.stringify(value ?? null);
}

/**
 * Bỏ dòng trùng khóa. API Find đôi khi trả cùng một dòng sheet hai lần (đã gặp ở NHANSU: cùng
 * SỐ HĐ, cùng _RowNumber), nếu không lọc thì một người bị tính lương hai lần.
 */
export function dedupeByKey(rows, keyColumn) {
    const seen = new Set();
    return rows.filter((row) => {
        const key = String(row?.[keyColumn] ?? '').trim();
        if (!key) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}
